import { Box, Divider, List, Paper, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo } from 'react';
import { Fighter } from '../../../../../../../corelogic/domain/models/fighter.model';
import { buildFighterKey } from '../../../../../../../corelogic/domain/utils/fighterImage.utils';
import { useAllFighterImagesState } from '../../../../hooks/useFighterImage';
import { FighterImageService } from '../../../../services/FighterImage.service';
import RankIcon from '../../../Shared/GetRankIcon';
import RankedFighterListItem from './RankedFighterListItem';

// Animation variant
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

// Style definitions
const sectionHeaderStyle = {
  p: 2,
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  bgcolor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
};

const sectionTitleStyle = {
  fontFamily: '"Bebas Neue", sans-serif',
  fontSize: 'clamp(0.8rem, 3vw, 1.5rem)',
};

const contentBoxStyle = {
  p: 2,
  height: 'calc(100% - 64px)',
  overflow: 'auto',
};

const paperStyle = {
  bgcolor: 'rgba(0, 0, 0, 0.85)',
  overflow: 'hidden',
  height: '500px',
};

interface SectionHeaderProps {
  icon?: React.ReactNode;
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon &&
      React.cloneElement(icon as React.ReactElement, {
        sx: { color: '#cc0000' },
      })}
    <Typography
      variant="h5"
      sx={{ ...sectionTitleStyle, fontSize: 'clamp(1.2rem, 3vw, 1.5rem)' }}
    >
      {title}
    </Typography>
  </Box>
);

interface RankedFighterSectionProps {
  title: string;
  icon: React.ReactNode;
  items: Array<{ fighter: Fighter; points: number; date: Date }>;
  showMedals?: boolean;
  onFighterClick: (fighter: Fighter) => void;
}

const RankedFighterSection: React.FC<RankedFighterSectionProps> = ({
  title,
  icon,
  items,
  showMedals = true,
  onFighterClick,
}) => {
  // Check if all fighters have 0 points
  const nothingToDisplay = useMemo(() => {
    return items.length === 0 || items.every((item) => item.points === 0);
  }, [items]);

  // Memoize the fighters array to prevent infinite loop in useAllFighterImagesState
  const memoizedFighters = useMemo(() => {
    return items.map((item) => item.fighter);
  }, [items]);

  // Load fighter images from S3
  const { fighterImages, loadingStates } =
    useAllFighterImagesState(memoizedFighters);

  // Request fighter images for all fighters
  useEffect(() => {
    items.forEach(({ fighter }) => {
      FighterImageService.retrieveFighterImage(
        fighter.firstname,
        fighter.lastname,
        fighter.dob,
        fighter.gender,
      );
    });
  }, [items]);

  return (
    <motion.div variants={item}>
      <Paper sx={paperStyle}>
        <Box sx={sectionHeaderStyle}>
          <SectionHeader icon={icon} title={title} />
        </Box>
        <Box sx={contentBoxStyle}>
          {nothingToDisplay ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  color: '#cccccc',
                  fontFamily: 'Rubik Mono One',
                  fontSize: 'clamp(0.5rem, 1vw, 1.5rem)',
                  maxWidth: '80%',
                  lineHeight: 1.4,
                }}
              >
                Fighters will be displayed once the first fight of the season
                completes!
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box sx={{ flex: 1, height: '100%', width: '100%' }}>
                <List>
                  {items.slice(0, 5).map((item, index) => {
                    const fighterKey = buildFighterKey(
                      item.fighter.firstname,
                      item.fighter.lastname,
                      item.fighter.dob,
                    );
                    const fighterImage = fighterKey
                      ? fighterImages[fighterKey]
                      : null;
                    const isImageLoading = fighterKey
                      ? loadingStates[fighterKey] || false
                      : false;

                    return (
                      <RankedFighterListItem
                        key={`${item.fighter.id}-${index}`}
                        index={index}
                        fighter={item.fighter}
                        points={item.points}
                        date={item.date}
                        onClick={() => onFighterClick(item.fighter)}
                        rankIcon={<RankIcon rank={index} />}
                        fighterImage={fighterImage}
                        isImageLoading={isImageLoading}
                      />
                    );
                  })}
                </List>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  height: 'auto',
                  display: { xs: 'none', sm: 'block' },
                }}
              />
              <Box sx={{ flex: 1, height: '100%', width: '100%' }}>
                <List>
                  {items.slice(5).map((item, index) => {
                    const fighterKey = buildFighterKey(
                      item.fighter.firstname,
                      item.fighter.lastname,
                      item.fighter.dob,
                    );
                    const fighterImage = fighterKey
                      ? fighterImages[fighterKey]
                      : null;
                    const isImageLoading = fighterKey
                      ? loadingStates[fighterKey] || false
                      : false;

                    return (
                      <RankedFighterListItem
                        key={`${item.fighter.id}-${index + 5}`}
                        index={index + 5}
                        fighter={item.fighter}
                        points={item.points}
                        date={item.date}
                        onClick={() => onFighterClick(item.fighter)}
                        rankIcon={<RankIcon rank={index + 5} />}
                        fighterImage={fighterImage}
                        isImageLoading={isImageLoading}
                      />
                    );
                  })}
                </List>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </motion.div>
  );
};

export default RankedFighterSection;
